import React from "react";
import { Box, Button, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { InviteLink as InviteLinkType, User, UserRole, UserSize } from "@/app/graphql/codegen";
import { InviteLinkRow } from "./InviteLinkManagementRow";
import { UserManagementRow } from "./UserManagementRow";
import AddIcon from "@mui/icons-material/Add";
import { icon } from "@/app/style/icons";

// Use more general type definitions to avoid conflicts
interface SeatManagementRowProps {
    index: number;
    size: UserSize;
    user?: any; // Use any to avoid type issues
    inviteLink?: any; // Use any to avoid type issues
    onCreateInvite: (size: UserSize) => void;
    onDeleteSeat: (size: UserSize) => void;
    onUserQuotaChange: (userId: string, quota: number) => void;
    onUserRoleChange: (userId: string, role: UserRole) => void;
    onUserSizeChange: (userId: string, size: "S" | "M" | "L") => void;
    onUserDelete: (user: any) => void;
    onUserSwap?: (user: any) => void;
    onInviteLinkDelete: (inviteLink: any) => void;
    onInviteLinkSwap?: (inviteLink: any) => void;
    isQuotaInvalid?: boolean;
    totalSeats: number;
    isAdmin: boolean;
}

export const SeatManagementRow: React.FC<SeatManagementRowProps> = ({
    index,
    size,
    user,
    inviteLink,
    onCreateInvite,
    onDeleteSeat,
    onUserQuotaChange,
    onUserRoleChange,
    onUserSizeChange,
    onUserDelete,
    onUserSwap,
    onInviteLinkDelete,
    onInviteLinkSwap,
    isQuotaInvalid,
    totalSeats,
    isAdmin,
}) => {
    const theme = useTheme();
    // The seat can be in one of three states: empty, occupied by a user, or occupied by an invite link
    const isEmpty = !user && !inviteLink;

    // Common size badge component used in all states
    const SizeBadge = () => (
        <Box
            sx={{
                position: "absolute",
                top: 0,
                right: 0,
                bgcolor: theme.palette.semantic.planSize[size],
                color: "white",
                px: 1.5,
                py: 0.5,
                borderBottomLeftRadius: 8,
                borderTopRightRadius: 8,
                fontWeight: "bold",
                zIndex: 1,
            }}
        >
            {size}
        </Box>
    );

    // Configure the wrapper box styles based on the state
    const getWrapperStyles = () => {
        const baseStyles = {
            position: "relative",
            mb: 2,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
        };

        if (isEmpty) {
            return {
                ...baseStyles,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 2,
                border: 1,
                borderColor: theme.palette.semantic.planSize[size],
                borderRadius: 1,
                backgroundColor: "rgba(0,0,0,0.01)",
            };
        }

        if (user) {
            return {
                ...baseStyles,
                "& > div:first-of-type": {
                    borderColor: isQuotaInvalid ? "error.main" : theme.palette.semantic.planSize[size],
                },
            };
        }

        // For invite link
        return {
            ...baseStyles,
            "& > div:first-of-type": {
                borderColor: theme.palette.semantic.planSize[size],
            },
        };
    };

    return (
        <Box sx={getWrapperStyles()}>
            {/* Empty state content */}
            {isEmpty && (
                <>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={() => onCreateInvite(size)}>
                        Einladungslink erstellen
                    </Button>

                    {isAdmin && (
                        <Tooltip title="Sitzplatz entfernen (Debug)" placement="bottom-end">
                            <IconButton size="small" color="error" onClick={() => onDeleteSeat(size)} sx={{ position: "absolute", bottom: 8, right: 8 }}>
                                <Box sx={{ fontSize: "0.75rem" }}>-</Box>
                            </IconButton>
                        </Tooltip>
                    )}
                </>
            )}

            {/* User content */}
            {user && (
                <UserManagementRow
                    user={user}
                    quota={user.pageQuota || 0}
                    onQuotaChange={(value) => onUserQuotaChange(user.id, value)}
                    role={(user.role || UserRole.Editor) as UserRole}
                    onRoleChange={(role) => onUserRoleChange(user.id, role)}
                    size={(user.size || size) as UserSize}
                    onSizeChange={() => {}} // Disabled since size is fixed by seat
                    isOwner={isAdmin}
                    onDeleteClick={() => onUserDelete(user)}
                    onSwapClick={onUserSwap ? () => onUserSwap(user) : undefined}
                    isQuotaInvalid={isQuotaInvalid}
                    disableSizeEdit
                    sizeColor={theme.palette.semantic.planSize[size]}
                />
            )}

            {/* Invite link content */}
            {inviteLink && (
                <InviteLinkRow 
                    inviteLink={inviteLink} 
                    onDeleteClick={() => onInviteLinkDelete(inviteLink)} 
                    onSwapClick={onInviteLinkSwap ? () => onInviteLinkSwap(inviteLink) : undefined}
                    sizeColor={theme.palette.semantic.planSize[size]} 
                />
            )}

            {/* Size Badge - common in all states */}
            <SizeBadge />
        </Box>
    );
};
